/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { Stack } from '@fluentui/react';
import {
  Button,
  Caption1Stronger,
  Caption1,
  mergeClasses,
  Divider,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';
import {
  EyeRegular,
  ThumbLikeRegular,
  ThumbDislikeRegular,
  ThumbLikeFilled,
  ThumbDislikeFilled,
} from '@fluentui/react-icons';
import {
  LineChart,
} from '@fluentui/react-charting';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';

import {
  PERFORMANCE_INSIGHTS_ISSUE,
  PERFORMANCE_INSIGHTS_TYPE,
  PERFORMANCE_INSIGHTS_POPUP_CONFIG,
  RADIO_VALUE,
  PERFORMANCE_INSIGHTS_ACTION,
} from './constants/consts';
import { getColor } from './util/util';

export const BidDetail = (props) => {
  const {
    classes,
    newI18n,
    allData,
    onClickSaveBid,
    setIsBidLikeSelected,
    setIsBidDislikeSelected,
    isBidLikeSelected,
    isBidDislikeSelected,
    chartClasses,
    setRadioValue,
    radioValue,
  } = props;

  const bidResult = [];
  bidResult.push({
    legend: 'Actual',
    data: allData[PERFORMANCE_INSIGHTS_ISSUE.BID].actualBidPoints,
    color: getColor('Actual'),
    lineOptions: {
      strokeWidth: '1px',
    },
  });
  bidResult.push({
    legend: 'Projected',
    legendShape: 'dottedLine',
    data: allData[PERFORMANCE_INSIGHTS_ISSUE.BID].estimatedBidPoints,
    color: getColor('Projected'),
    lineOptions: {
      strokeDasharray: '2px',
      strokeDashoffset: '-1',
      strokeLinecap: 'butt',
      strokeWidth: '1px',
    },
  });
  const bidChartData = {
    chartTitle: newI18n.getString(
      PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].chartTitle,
      allData[PERFORMANCE_INSIGHTS_ISSUE.BID]
    ),
    lineChartData: bidResult,
  };
  return (
    <Stack className={classes.healthCheck}>
      <Caption1Stronger className={classes.title}>{newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].title)}</Caption1Stronger>
      <Stack horizontal className={classes.body} tokens={{ childrenGap: 24 }}>
        <Stack>
          <Caption1 className={classes.chartTitle}>
            {
              newI18n.getString(_TL_('Conversions between {{start}} to {{end}}'), { start: allData[PERFORMANCE_INSIGHTS_ISSUE.BID].startDate, end: allData[PERFORMANCE_INSIGHTS_ISSUE.BID].endDate })
            }
          </Caption1>
          <LineChart
            data={bidChartData}
            legendsOverflowText="Overflow Items"
            className={mergeClasses('inline-chart', chartClasses.inlineChart, classes.chart)}
            allowMultipleShapesForPoints
            tickFormat="%m/%d"
            tickValues={_.map(allData[PERFORMANCE_INSIGHTS_ISSUE.BID].actualBidPoints, point => point.x)}
          />
          <Stack horizontal className={classes.bottom}>
            <EyeRegular className={classes.viewIcon} />
            <Caption1>
              {
                newI18n.getString(_TL_('The issue was detected on {{start}}'), { start: allData[PERFORMANCE_INSIGHTS_ISSUE.BID].startDate })
              }
            </Caption1>
          </Stack>
        </Stack>
        <Stack className={classes.surface} tokens={{ childrenGap: 12 }}>
          <Caption1>{newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].description, allData[PERFORMANCE_INSIGHTS_ISSUE.BID])}</Caption1>
          <Caption1>{newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].pitching)}</Caption1>
          <Divider className={classes.divider} />
          <Caption1Stronger>{newI18n.getString(_TL_('You can fix this issue by:'))}</Caption1Stronger>
          <RadioGroup
            className={classes.radioGroup}
            defaultValue={radioValue}
            onChange={(e, data) => setRadioValue(data.value)}
          >
            <Radio className={classes.radio} value={RADIO_VALUE.suggest} label={newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].actionDetails[0], allData[PERFORMANCE_INSIGHTS_ISSUE.BID])} />
            {allData[PERFORMANCE_INSIGHTS_ISSUE.BID].verticalTarget &&
              allData[PERFORMANCE_INSIGHTS_ISSUE.BID].verticalPercentage &&
              <Radio className={classes.radio} value={RADIO_VALUE.vertical} label={newI18n.getString(PERFORMANCE_INSIGHTS_POPUP_CONFIG[PERFORMANCE_INSIGHTS_TYPE.ISSUE][PERFORMANCE_INSIGHTS_ISSUE.BID].actionDetails[1], allData[PERFORMANCE_INSIGHTS_ISSUE.BID])} />
            }
          </RadioGroup>
          <Stack className={classes.action} horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
            <Button className={classes.saveButton} appearance="primary" onClick={() => onClickSaveBid()}>{newI18n.getString(PERFORMANCE_INSIGHTS_ACTION.SAVE)}</Button>
            <LearnMoreLink
              linkText={newI18n.getString(_TL_('Learn more'))}
              topic="56786"
              isUnderlineLink={false}
            />
          </Stack>
          <Stack className="feedback" horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
            <Button
              className="feedbackButton"
              icon={isBidLikeSelected ? <ThumbLikeFilled /> : <ThumbLikeRegular />}
              appearance="transparent"
              onClick={() => {
                setIsBidLikeSelected(!isBidLikeSelected);
              }}
            />
            <Button
              className="feedbackButton"
              icon={isBidDislikeSelected ? <ThumbDislikeFilled /> : <ThumbDislikeRegular />}
              appearance="transparent"
              onClick={() => {
                setIsBidDislikeSelected(!isBidDislikeSelected);
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

BidDetail.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  allData: PropTypes.shape({}).isRequired,
  onClickSaveBid: PropTypes.func.isRequired,
  setIsBidLikeSelected: PropTypes.func.isRequired,
  setIsBidDislikeSelected: PropTypes.func.isRequired,
  isBidLikeSelected: PropTypes.bool.isRequired,
  isBidDislikeSelected: PropTypes.bool.isRequired,
  chartClasses: PropTypes.shape({}).isRequired,
  setRadioValue: PropTypes.func.isRequired,
  radioValue: PropTypes.string.isRequired,
};
